"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faXmark } from "@fortawesome/free-solid-svg-icons";
import CommonCalendar from "@/components/utils/calendar/CommonCalendar";
import type { CreateWaveDatesConfig } from "@/types/waves.types";
import TimePicker from "@/components/common/TimePicker";
import TooltipIconButton from "@/components/common/TooltipIconButton";
import { CREATE_WAVE_VALIDATION_ERROR } from "@/helpers/waves/create-wave.validation";
import { clampApproveWaveEndDate, getEarliestApproveWaveEndTimestamp } from "./approveWaveDates.helpers";
interface CreateWaveDatesApproveEndProps {
  readonly dates: CreateWaveDatesConfig;
  readonly errors: CREATE_WAVE_VALIDATION_ERROR[];
  readonly setDates: (dates: CreateWaveDatesConfig) => void;
}
const formatDateTime = (timestamp: number) => new Date(timestamp).toLocaleString("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true
});
const END_DATE_TOOLTIP_TEXT = "Choose when the approve wave closes. Leave it blank to keep the wave open " + "until max winners is reached, or indefinitely if max winners is blank.";
export default function CreateWaveDatesApproveEnd(t0) {
  const $ = _c(75);
  const {
    dates,
    errors,
    setDates
  } = t0;
  const endDate = dates.endDate;
  const selectedEndDate = endDate !== null && Number.isFinite(endDate) ? endDate : null;
  const hasSelectedEndDate = selectedEndDate !== null;
  let t1;
  if ($[0] !== errors) {
    t1 = errors.includes(CREATE_WAVE_VALIDATION_ERROR.END_DATE_MUST_BE_AFTER_VOTING_START_DATE);
    $[0] = errors;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasEndBeforeStartError = t1;
  let t2;
  if ($[2] !== dates.submissionStartDate) {
    t2 = getEarliestApproveWaveEndTimestamp(dates.submissionStartDate);
    $[2] = dates.submissionStartDate;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const earliestValidEndTimestamp = t2;
  const displayedTimestamp = selectedEndDate ?? earliestValidEndTimestamp;
  let t3;
  let t4;
  if ($[4] !== earliestValidEndTimestamp) {
    const minDate = new Date(earliestValidEndTimestamp);
    t3 = minDate.getHours();
    t4 = minDate.getMinutes();
    $[4] = earliestValidEndTimestamp;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = {
      hours: t3,
      minutes: t4
    };
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const minTime = t5;
  let t6;
  if ($[10] !== displayedTimestamp) {
    t6 = new Date(displayedTimestamp);
    $[10] = displayedTimestamp;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const displayedDate = t6;
  const earliestValidEndDate = new Date(earliestValidEndTimestamp);
  const isSameDayAsEarliestValidEnd = hasSelectedEndDate && displayedDate.toDateString() === earliestValidEndDate.toDateString();
  let t7;
  if ($[12] !== dates || $[13] !== displayedTimestamp || $[14] !== setDates) {
    t7 = timestamp => {
      const currentDate = new Date(displayedTimestamp);
      const newDate = new Date(timestamp);
      const currentHours = currentDate.getHours();
      const currentMinutes = currentDate.getMinutes();
      newDate.setHours(currentHours, currentMinutes, 0, 0);
      const newTimestamp = clampApproveWaveEndDate(newDate, dates.submissionStartDate).getTime();
      setDates({
        ...dates,
        endDate: newTimestamp
      });
    };
    $[12] = dates;
    $[13] = displayedTimestamp;
    $[14] = setDates;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleDateSelection = t7;
  let t8;
  if ($[16] !== dates || $[17] !== displayedTimestamp || $[18] !== setDates) {
    t8 = (hours, minutes) => {
      const nextDate = new Date(displayedTimestamp);
      nextDate.setHours(hours, minutes, 0, 0);
      const newTimestamp_0 = clampApproveWaveEndDate(nextDate, dates.submissionStartDate).getTime();
      setDates({
        ...dates,
        endDate: newTimestamp_0
      });
    };
    $[16] = dates;
    $[17] = displayedTimestamp;
    $[18] = setDates;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const handleTimeChange = t8;
  let t9;
  if ($[20] !== dates || $[21] !== setDates) {
    t9 = () => {
      setDates({
        ...dates,
        endDate: null
      });
    };
    $[20] = dates;
    $[21] = setDates;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const handleClearEndDate = t9;
  let t10;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div><div className="tw-flex tw-items-center tw-gap-x-2"><h3 className="tw-mb-0 tw-text-base tw-font-semibold tw-text-iron-300">Wave End</h3><TooltipIconButton icon={faInfoCircle} tooltipText={END_DATE_TOOLTIP_TEXT} tooltipPosition="bottom" tooltipWidth="tw-w-80" /></div><p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-iron-400">Optional. Leave blank for no end date.</p></div>;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <p className="tw-mb-0 tw-text-xs tw-text-iron-300/70">Wave Ends</p>;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const t12 = `tw-mb-0 tw-text-sm tw-font-medium ${hasEndBeforeStartError ? "tw-text-error" : "tw-text-iron-50"}`;
  let t13;
  if ($[25] !== selectedEndDate) {
    t13 = selectedEndDate !== null ? formatDateTime(selectedEndDate) : "No end date";
    $[25] = selectedEndDate;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t12 || $[28] !== t13) {
    t14 = <p className={t12}>{t13}</p>;
    $[27] = t12;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== handleClearEndDate || $[31] !== hasSelectedEndDate) {
    t15 = hasSelectedEndDate && <button type="button" aria-label="Clear end date" onClick={handleClearEndDate} className="tw-flex tw-size-6 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-iron-600 tw-bg-iron-800 tw-text-iron-300 tw-transition tw-duration-300 hover:tw-border-primary-400 hover:tw-text-primary-300"><FontAwesomeIcon icon={faXmark} className="tw-size-3" /></button>;
    $[30] = handleClearEndDate;
    $[31] = hasSelectedEndDate;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t14 || $[34] !== t15) {
    t16 = <div className="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-start sm:tw-justify-between">{t10}<div className="tw-rounded-lg tw-bg-iron-700/40 tw-px-3 tw-py-2 tw-shadow-md">{t11}<div className="tw-flex tw-items-center tw-gap-x-2">{t14}{t15}</div></div></div>;
    $[33] = t14;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== hasEndBeforeStartError) {
    t17 = hasEndBeforeStartError && <div id="approve-wave-end-date-error" role="alert" className="tw-mb-3 tw-flex tw-items-center tw-gap-x-2 tw-text-xs tw-font-medium tw-text-error"><svg className="tw-size-4 tw-flex-shrink-0" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg><span>Wave end must be after wave start.</span></div>;
    $[36] = hasEndBeforeStartError;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <p className="tw-mb-2 tw-text-sm tw-font-medium tw-text-iron-300">Select End Date:</p>;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== displayedDate) {
    t19 = displayedDate.getMonth();
    $[39] = displayedDate;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== displayedDate) {
    t20 = displayedDate.getFullYear();
    $[41] = displayedDate;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== earliestValidEndTimestamp || $[44] !== handleDateSelection || $[45] !== selectedEndDate || $[46] !== t19 || $[47] !== t20) {
    t21 = <div className="tw-w-full">{t18}<CommonCalendar initialMonth={t19} initialYear={t20} selectedTimestamp={selectedEndDate} minTimestamp={earliestValidEndTimestamp} maxTimestamp={null} setSelectedTimestamp={handleDateSelection} /></div>;
    $[43] = earliestValidEndTimestamp;
    $[44] = handleDateSelection;
    $[45] = selectedEndDate;
    $[46] = t19;
    $[47] = t20;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <p className="tw-mb-2 tw-text-sm tw-font-medium tw-text-iron-300">Select End Time:</p>;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== displayedDate) {
    t23 = displayedDate.getHours();
    $[50] = displayedDate;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== displayedDate) {
    t24 = displayedDate.getMinutes();
    $[52] = displayedDate;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  const t25 = isSameDayAsEarliestValidEnd ? minTime : null;
  const t26 = !hasSelectedEndDate;
  let t27;
  if ($[54] !== handleTimeChange || $[55] !== t23 || $[56] !== t24 || $[57] !== t25 || $[58] !== t26) {
    t27 = <TimePicker hours={t23} minutes={t24} onTimeChange={handleTimeChange} minTime={t25} disabled={t26} />;
    $[54] = handleTimeChange;
    $[55] = t23;
    $[56] = t24;
    $[57] = t25;
    $[58] = t26;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== earliestValidEndTimestamp || $[61] !== hasSelectedEndDate) {
    t28 = !hasSelectedEndDate && <p className="tw-mt-2 tw-text-xs tw-text-iron-400">Pick an end date first. The earliest allowed end time is{" "}{formatDateTime(earliestValidEndTimestamp)}.</p>;
    $[60] = earliestValidEndTimestamp;
    $[61] = hasSelectedEndDate;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  let t29;
  if ($[63] !== t27 || $[64] !== t28) {
    t29 = <div className="tw-w-full">{t22}{t27}{t28}</div>;
    $[63] = t27;
    $[64] = t28;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  let t30;
  if ($[66] !== t21 || $[67] !== t29) {
    t30 = <div className="tw-grid tw-grid-cols-1 tw-gap-x-10 tw-gap-y-8 md:tw-grid-cols-2">{t21}{t29}</div>;
    $[66] = t21;
    $[67] = t29;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== t17 || $[70] !== t30) {
    t31 = <div className="tw-mt-5">{t17}{t30}</div>;
    $[69] = t17;
    $[70] = t30;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] !== t16 || $[73] !== t31) {
    t32 = <section className="tw-rounded-xl tw-bg-iron-900 tw-px-5 tw-pb-5 tw-pt-5 tw-shadow-sm tw-ring-1 tw-ring-iron-700/50">{t16}{t31}</section>;
    $[72] = t16;
    $[73] = t31;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  return t32;
}